"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Ruler, ClipboardCheck, TrendingDown, ShieldCheck } from "lucide-react";
import { StructuredData } from "@/components/common";
import ConsultationCTA from "@/components/services/ConsultationCTA";
import { ServiceFaq } from "@/components/services";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";

// The four measures that make up a baseline. Deliberately the same four we ask a
// client for at scoping, so the page documents the actual method rather than a
// marketing version of it.
const baselineMeasures = [
  {
    icon: Ruler,
    title: "Time per case",
    body: "How long one person takes on one instance of the task, measured across enough cases to be honest about the slow ones rather than the demo one.",
  },
  {
    icon: TrendingDown,
    title: "Error rate",
    body: "How often the manual process gets it wrong today. This number is almost always worse than anyone expects, and it is the single most useful thing scoring produces.",
  },
  {
    icon: ClipboardCheck,
    title: "Correction cost",
    body: "What each error costs downstream — the rework, the senior review time, the customer call. An error that takes four minutes to catch and an hour to unwind are not the same error.",
  },
  {
    icon: ShieldCheck,
    title: "Escalation quality",
    body: "What happens on the cases the process cannot handle. A clean handoff to a person is a good outcome. A silent wrong answer is not.",
  },
];

const faqs = [
  {
    question: "What is Agent GPA?",
    answer:
      "Agent GPA is how we decide whether an AI agent is worth shipping. Before any agent is built, we score the existing manual process on four measures: time per case, error rate, correction cost and escalation quality. That becomes the baseline. Every version of the agent is then scored against the same cases, and it does not go live until it outscores the process it would replace. The name is ours; the practice is ordinary evaluation discipline applied earlier than most vendors apply it.",
  },
  {
    question: "Why score the manual process first?",
    answer:
      "Because without a baseline, 'the agent is 92% accurate' means nothing. Ninety-two per cent is excellent if your team runs at 80% and unacceptable if they run at 99%. Scoring the manual process first is also the step that most often kills a project — and killing it at scoping costs a scoping conversation, while killing it after launch costs a quarter.",
  },
  {
    question: "How often does scoring say don't build it?",
    answer:
      "Often enough that we consider it a feature of the method rather than a failure of it. If a process is already 99% accurate and takes four minutes, an agent is not the win — the money is better spent elsewhere. The same applies when the data feeding the process is wrong: connecting an agent to bad source data produces confidently wrong answers faster.",
  },
  {
    question: "Do regulated businesses need this on paper?",
    answer:
      "Yes, and it is usually the reason they ask. If you are under Bank Negara Malaysia or MAS scrutiny, 'the vendor said it works well' is not an answer. A scored evaluation with a documented baseline, a per-version record, and a named human accountable for sign-off is the artefact your risk function will ask for. We produce it as a matter of course rather than on request.",
  },
  {
    question: "Who signs off the score?",
    answer:
      "The Claude Certified Architect who designed the system. Not a delivery manager relaying numbers, and not the client discovering them after go-live. You approve the baseline before we build and the scores before anything touches a customer or a ledger.",
  },
];

export default function AgentGpa() {
  const locale = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP). Below-the-fold sections keep the inView fade.
  const heroIn = {
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };
  const localisedFaqs = faqs.map(({ question, answer }) => ({
    question: t(locale, question),
    answer: t(locale, answer),
  }));

  return (
    <>
      <StructuredData type="faq" faqs={localisedFaqs} />

      <div className="bg-background text-neutral-emphasis">
        {/* Hero */}
        <section className="bg-background pb-16 pt-24 lg:pt-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div {...heroIn} className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {t(locale, "Our evaluation method")}
              </p>
              <h1 className="mt-5 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl">
                {t(locale, "Agent GPA: we do not ship an agent until it outscores the process it replaces")}
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-neutral-muted md:text-xl">
                {t(
                  locale,
                  "That sounds obvious. Almost nobody does it. The usual sequence is build the agent, demo the agent, deploy it, and discover six weeks later that the team quietly went back to the spreadsheet. Scoring first is what prevents that, and it is why we sometimes tell clients not to build at all.",
                )}
              </p>
            </motion.div>
          </div>
        </section>

        {/* The baseline */}
        <section className="border-y border-border bg-neutral-surface py-20 lg:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div {...heroIn} className="max-w-3xl">
              <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
                {t(locale, "First we score the humans")}
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "Before writing any agent, we measure the manual process on four things. Not to make a case for automation — to find out whether there is one.",
                )}
              </p>
            </motion.div>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {baselineMeasures.map(({ icon: Icon, title, body }) => (
                <motion.div
                  key={title}
                  {...heroIn}
                  className="flex gap-4 rounded-2xl border border-border bg-card p-6"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                      {t(locale, title)}
                    </h3>
                    <p className="mt-2 text-base leading-relaxed text-neutral-muted">
                      {t(locale, body)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Then the agent */}
        <section className="bg-background py-20 lg:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div {...inView} className="max-w-3xl">
              <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
                {t(locale, "Then every version of the agent is scored on the same cases")}
              </h2>
              <div className="mt-6 space-y-5 text-lg leading-relaxed text-neutral-muted">
                <p>
                  {t(
                    locale,
                    "The same case set, the same four measures, version after version. That is the whole idea, and the discipline is in using the same cases rather than the ones the agent happens to do well on.",
                  )}
                </p>
                <p>
                  {t(
                    locale,
                    "An agent goes live when it beats the baseline on the measures that matter for that workflow — and not before. Sometimes that is version two. Sometimes it is version nine. Occasionally it is never, and that is a result rather than a failure.",
                  )}
                </p>
              </div>
            </motion.div>

            <motion.div {...inView} className="mt-12 grid gap-6 md:grid-cols-3">
              {[
                {
                  h: "Sometimes the answer is do not build it",
                  p: "If a process is already 99% accurate and takes four minutes, an agent is not the win. We would rather say so at scoping than eleven weeks in.",
                },
                {
                  h: "The baseline is usually worse than anyone thinks",
                  p: "Manual three-way invoice matching does not run at 100%. Once you actually measure it, the bar the agent has to clear becomes honest.",
                },
                {
                  h: "Regulated clients need this on paper",
                  p: "Under Bank Negara or MAS scrutiny, “the vendor said it works well” is not an answer. A scored evaluation is the artefact your risk function asks for.",
                },
              ].map((card) => (
                <div key={card.h} className="rounded-2xl border border-border bg-card p-6">
                  <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                    {t(locale, card.h)}
                  </h3>
                  <p className="mt-2.5 text-base leading-relaxed text-neutral-muted">
                    {t(locale, card.p)}
                  </p>
                </div>
              ))}
            </motion.div>

            <motion.p {...inView} className="mt-10 max-w-3xl text-lg leading-relaxed text-neutral-muted">
              {t(locale, "Worked examples of agents built this way are in our ")}
              <Link
                href={getLocalizedPathname("/solutions/agent-use-cases/", locale)}
                className="font-medium text-primary underline underline-offset-4"
              >
                {t(locale, "agent use-case library")}
              </Link>
              {t(locale, ", and the build work itself is described on ")}
              <Link
                href={getLocalizedPathname("/services/ai-agents/", locale)}
                className="font-medium text-primary underline underline-offset-4"
              >
                {t(locale, "AI agent development")}
              </Link>
              {t(locale, ".")}
            </motion.p>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-neutral-surface py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <motion.h2
              {...inView}
              className="mb-10 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl"
            >
              {t(locale, "Questions about Agent GPA")}
            </motion.h2>
            <ServiceFaq items={localisedFaqs} />
          </div>
        </section>

        <ConsultationCTA
          title="Tell us the process you want scored"
          description="We will measure what it costs you today before proposing anything. If the numbers say an agent is not the answer, that is what we will tell you."
        />
      </div>
    </>
  );
}
